import { Text } from "@microbeta/ui";
import type React from "react";
import { type DragEvent, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineCloudUpload } from "react-icons/hi";
import styles from "./FileDropzone.module.scss";

interface FileDropzoneProps {
  onFileSelect?: (file: File) => void;
  onFilesSelect?: (files: File[]) => void;
  label?: string;
  hint?: string;
  className?: string;
  disabled?: boolean;
  uploadProgress?: number | null;
  fileName?: string | null;
  accept?: string;
  validateFile: (file: File) => boolean;
  multiple?: boolean;
  error?: string | null;
}

export const FileDropzone: React.FC<FileDropzoneProps> = ({
  onFileSelect,
  onFilesSelect,
  label,
  hint,
  className = "",
  disabled = false,
  uploadProgress = null,
  fileName = null,
  accept = "image/*",
  validateFile,
  multiple = false,
  error = null,
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const validFiles = Array.from(files).filter(validateFile);

      if (validFiles.length === 0) return;

      if (multiple && onFilesSelect) {
        onFilesSelect(validFiles);
      } else if (!multiple && onFileSelect) {
        onFileSelect(validFiles[0]);
      }
    },
    [onFileSelect, onFilesSelect, validateFile, multiple]
  );

  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
        e.dataTransfer.clearData();
      }
    },
    [handleFiles]
  );

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(e.target.files);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [handleFiles]
  );

  return (
    <button
      type="button"
      className={`${styles.dropzone} ${
        isDragging ? styles.dragging : ""
      } ${className} ${disabled ? styles.disabled : ""}`}
      onDragEnter={disabled ? undefined : handleDragEnter}
      onDragLeave={disabled ? undefined : handleDragLeave}
      onDragOver={disabled ? undefined : handleDragOver}
      onDrop={disabled ? undefined : handleDrop}
      onClick={disabled ? undefined : handleClick}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) handleClick();
      }}
      aria-label={label}
      disabled={disabled}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        style={{ display: "none" }}
        disabled={disabled}
        multiple={multiple}
      />
      {uploadProgress !== null ? (
        <div className={styles.progressContainer}>
          <Text variant="small">
            {fileName || t("imageUploader.uploadingFile")}
          </Text>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${uploadProgress}%` }}
              role="progressbar"
              aria-valuenow={uploadProgress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Upload progress: ${uploadProgress}%`}
            />
          </div>
          <Text variant="small">{uploadProgress}%</Text>
        </div>
      ) : (
        <div className={styles.uploadContent}>
          <div className={styles.uploadIcon}>
            <HiOutlineCloudUpload />
          </div>
          <div className={styles.uploadText}>
            <Text variant="medium" className={styles.uploadLabel}>
              {label || t("imageUploader.dragDropLabel")}
            </Text>
            <Text variant="small" className={styles.uploadHint}>
              {hint || t("imageUploader.fileTypeHint")}
            </Text>
            {error && (
              <Text variant="small" className={styles.error}>
                {error}
              </Text>
            )}
          </div>
        </div>
      )}
    </button>
  );
};
